import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink } from "lucide-react";
import ScrollReveal from "./ScrollReveal";
import ExternalSiteDialog, { type ExternalSiteLink } from "./ExternalSiteDialog";
import logo1001vies from "@/assets/logos/1001vies.webp";
import logoParisHabitat from "@/assets/logos/paris-habitat.webp";
import logoRivp from "@/assets/logos/rivp.webp";
import logoSncfImmobilier from "@/assets/logos/sncf-immobilier.webp";
import logoSodexo from "@/assets/logos/sodexo.webp";
import logoCram from "@/assets/logos/cram.webp";
import logoFlunch from "@/assets/logos/flunch.webp";
import logoAxa from "@/assets/logos/axa.webp";

type BadgeLink = {
  label: string;
  title?: string;
  url?: string;
  embeddable?: boolean;
};

const logos = [
  { src: logo1001vies, alt: "1001 Vies Habitat", scale: 1.9 },
  { src: logoParisHabitat, alt: "Paris Habitat", scale: 1.2 },
  { src: logoRivp, alt: "RIVP", scale: 1.2 },
  { src: logoSncfImmobilier, alt: "SNCF Immobilier", scale: 1.4 },
  { src: logoSodexo, alt: "Sodexo EM", scale: 1.2 },
  { src: logoCram, alt: "CRAM", scale: 1.8 },
  { src: logoFlunch, alt: "Flunch", scale: 1.9 },
  { src: logoAxa, alt: "AXA", scale: 1.7 },
];

const badges: BadgeLink[] = [
  { label: "🏛️ Conformité ANSES" },
  { label: "📋 Décret Tertiaire 2030" },
  { label: "🔒 Décret BACS" },
  {
    label: "🚀 Village By CA Paris",
    title: "Village By CA Paris",
    url: "https://paris.levillagebyca.com/",
    embeddable: false,
  },
  {
    label: "🔬 Aquanova",
    title: "Aquanova",
    url: "https://www.poleaquanova.fr/",
  },
  {
    label: "🌐 Cap Digital",
    title: "Cap Digital",
    url: "https://www.capdigital.com/",
  },
  {
    label: "🎓 HEC Paris",
    title: "HEC Paris",
    url: "https://www.hec.edu/fr",
    embeddable: false,
  },
  {
    label: "🎓 Paris Dauphine",
    title: "Paris Dauphine",
    url: "https://dauphine.psl.eu/",
  },
  {
    label: "💧 Cluster EMS",
    title: "Cluster EMS",
    url: "https://www.clusterems.org/",
  },
];

const SocialProofSection = () => {
  const { t } = useTranslation();
  const [selectedSite, setSelectedSite] = useState<ExternalSiteLink | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const openSiteDialog = (badge: BadgeLink) => {
    if (!badge.url) return;

    setSelectedSite({
      label: badge.title ?? badge.label,
      url: badge.url,
      embeddable: badge.embeddable,
    });
    setDialogOpen(true);
  };

  return (
    <>
      <section id="social-proof" className="py-8 lg:py-14 bg-card relative" aria-labelledby="social-proof-heading">
        <div className="container mx-auto px-4 space-y-4 lg:space-y-6">
          <ScrollReveal>
            <div className="space-y-4 text-center">
              <p className="section-label">{t("socialProof.label")}</p>
              <h2 id="social-proof-heading" className="font-display text-3xl lg:text-5xl text-foreground">
                {t("socialProof.title")}
              </h2>
            </div>
          </ScrollReveal>

          <div className="relative overflow-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-card to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-card to-transparent z-10 pointer-events-none" />
            <div className="flex animate-marquee gap-28 items-center w-max">
              {[...logos, ...logos].map((l, i) => (
                <div key={`${l.alt}-${i}`} className="flex-shrink-0 flex items-center justify-center h-24 w-48 rounded-xl bg-white/95 px-5 py-3 opacity-90 hover:opacity-100 transition-opacity duration-300">
                  <img src={l.src} alt={`Logo ${l.alt}`} className="w-auto max-w-full object-contain" style={{ height: `${48 * l.scale}px` }} loading="lazy" />
                </div>
              ))}
            </div>
          </div>

          <ScrollReveal>
            <div className="flex flex-wrap justify-center gap-3 mt-8 md:mt-12">
              {badges.map((badge) =>
                badge.url ? (
                  <button
                    key={badge.label}
                    type="button"
                    onClick={() => openSiteDialog(badge)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-primary/10 bg-secondary px-3 py-1.5 text-xs font-body text-muted-foreground transition-colors hover:border-primary/30 hover:text-foreground"
                  >
                    {badge.label}
                    <ExternalLink size={11} className="opacity-50" />
                  </button>
                ) : (
                  <span key={badge.label} className="text-xs font-body text-muted-foreground bg-secondary px-3 py-1.5 rounded-full border border-primary/10">
                    {badge.label}
                  </span>
                ),
              )}
            </div>
          </ScrollReveal>

          <ScrollReveal>
            <div className="card-surface p-8 border-glow max-w-3xl mx-auto">
              <blockquote className="text-foreground font-body text-lg italic leading-relaxed">
                {t("socialProof.testimonial")}
              </blockquote>
              <p className="mt-4 text-sm text-muted-foreground font-body">{t("socialProof.testimonialAuthor")}</p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <ExternalSiteDialog open={dialogOpen} onOpenChange={setDialogOpen} site={selectedSite} />
    </>
  );
};

export default SocialProofSection;
